import { useWalletProviders } from "@/context/walletHookProviders";
import { Wallet, WalletProvider } from "../Models/WalletProvider";
import { useCallback, useMemo } from "react";
import { Network } from "@train-protocol/sdk";

export type WalletPurpose = "autofill" | "withdrawal" | "asSource"

export default function useWallet(network?: Network | null | undefined, purpose?: WalletPurpose) {
    const walletProviders = useWalletProviders()

    const provider = useMemo(() => network ? resolveProvider(network, walletProviders, purpose) : undefined, [network, purpose, walletProviders])

    const wallets = useMemo(() => {
        let connectedWallets: Wallet[] = [];
        walletProviders.forEach((provider) => {

            const w = provider.connectedWallets?.map(wallet => {
                return resolveWallet(wallet, network, provider, purpose)
            });
            connectedWallets = w ? [...connectedWallets, ...w] : [...connectedWallets];
        });
        return connectedWallets;
    }, [walletProviders, network]);

    const unAvailableWallets = useMemo(() => {
        return wallets.filter(wallet => wallet.isNotAvailable)
    }, [wallets])

    const availableWallets = useMemo(() => {
        return wallets.filter(wallet => !wallet.isNotAvailable)
    }, [wallets])

    const getProvider = useCallback((network: Network, purpose: WalletPurpose) => {
        return network && resolveProvider(network, walletProviders, purpose)
    }, [walletProviders, purpose]);

    const res = useMemo(() => ({
        wallets: availableWallets,
        unAvailableWallets,
        provider,
        providers: walletProviders,
        getProvider
    }), [wallets, provider, walletProviders, getProvider])

    return res
}

const resolveProvider = (network: Network | null | undefined, walletProviders: WalletProvider[], purpose?: WalletPurpose) => {
    if (!purpose || !network) return

    let provider: WalletProvider | undefined = undefined
    switch (purpose) {
        case "withdrawal":
            provider = walletProviders.find(provider => provider.withdrawalSupportedNetworks?.includes(network.caip2Id))
            break;
        case "autofill":
            provider = walletProviders.find(provider => provider.autofillSupportedNetworks?.includes(network.caip2Id))
            break;
        case "asSource":
            provider = walletProviders.find(provider => provider.asSourceSupportedNetworks?.includes(network.caip2Id))
            break;
    }

    if (provider?.isNotAvailableCondition && purpose) {
        const availableWalletsForConnect = provider.availableWalletsForConnect?.filter(connector => (provider.isNotAvailableCondition && network?.caip2Id) ? !provider.isNotAvailableCondition(connector.id, network?.caip2Id, purpose) : true)
        const resolvedProvider = {
            ...provider,
            connectedWallets: provider.connectedWallets?.map(wallet => {
                return {
                    ...wallet,
                    isNotAvailable: (provider.isNotAvailableCondition && network?.caip2Id && wallet.internalId) ? provider.isNotAvailableCondition(wallet.internalId, network?.caip2Id, purpose) : false,
                }
            }),
            activeWallet: provider.activeWallet ? {
                ...provider.activeWallet,
                isNotAvailable: (network?.caip2Id) ? provider.isNotAvailableCondition(provider.activeWallet.id, network?.caip2Id, purpose) : false,
            } : undefined,
            availableWalletsForConnect
        }
        return resolvedProvider
    }

    return provider
}

const resolveWallet = (wallet: Wallet, network: Network | null | undefined, provider: WalletProvider, purpose?: WalletPurpose) => {

    if (provider.isNotAvailableCondition && network?.caip2Id && wallet.internalId && !purpose) {
        return {
            ...wallet,
            isNotAvailable: provider.isNotAvailableCondition(wallet.internalId, network?.caip2Id),
        }
    }

    if (purpose === "autofill") {
        return {
            ...wallet,
            isNotAvailable: !wallet.autofillSupportedNetworks?.some(n => n.toLowerCase() === network?.caip2Id.toLowerCase()),
        }
    } else if (purpose === "withdrawal") {
        return {
            ...wallet,
            isNotAvailable: !wallet.withdrawalSupportedNetworks?.some(n => n.toLowerCase() === network?.caip2Id.toLowerCase()),
        }
    } else if (purpose === "asSource") {
        return {
            ...wallet,
            isNotAvailable: !wallet.asSourceSupportedNetworks?.some(n => n.toLowerCase() === network?.caip2Id.toLowerCase()),
        }
    }

    return {
        ...wallet,
        isNotAvailable: false,
    }
}